/**
 * BasemapSwitcher — floating basemap quick-switch control.
 *
 * Renders a compact pill in the bottom-right of the map. Activating it
 * opens a tokenized popover listing every entry in the
 * `engines/maplibre/basemaps` registry. Selecting an option:
 *
 *   1. updates `useViewerStore.basemapToolCard.activeBasemapId` (the WP-3
 *      source of truth — see §7.1 of the masterplan),
 *   2. calls `MapLibreAdapter.setBasemap` (through `applyBasemap`) on the
 *      mounted host, and
 *   3. writes the preference through to `localStorage` so a hard reload
 *      keeps the choice.
 *
 * The component is consumed by `TwinShell` and the legacy
 * `RasterDemoRoute`; the full Card 1 surface (opacity, labels, brightness,
 * scale, north arrow, geocoder, screenshot/share) lives in
 * `components/basemap/BasemapToolCard.tsx`.
 *
 * Design-system compliance (WP-3 refactor):
 *   - The world-map emoji glyph (previously rendered inside the trigger
 *     button) is replaced by `<Icon name="map" size={20} />` per
 *     `geosoft-platform-design-system/docs/DESIGN_POLICY.md` §5 (no emoji
 *     icons in product UI).
 *   - All shadow / radius / spacing / typography literals removed; only
 *     `tokens.*` values and design primitives are used.
 *   - Absolute pixel offsets (`right: 16`, `bottom: 32`) replaced by
 *     `tokens.space.*` slots.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  BASEMAPS,
  type BasemapId,
  applyBasemap,
  savePreferredBasemapId,
} from '@/engines/maplibre/basemaps';
import { getMapLibreHostHandle } from '@/engines/maplibre/hostHandle';
import { Icon, tokens } from '@/design';
import { useViewerStore } from '@/state/useViewerStore';
import { useViewerSlice } from '@/state/useViewerSlice';

export interface BasemapSwitcherProps {
  /** Container element that hosts the MapLibre map. */
  container: HTMLElement | null;
}

export function BasemapSwitcher({ container }: BasemapSwitcherProps) {
  const [open, setOpen] = useState(false);
  const activeId = useViewerSlice((s) => s.basemapToolCard.activeBasemapId);
  const setBasemapToolCard = useViewerStore((s) => s.setBasemapToolCard);
  const popoverRef = useRef<HTMLDivElement>(null);

  const activeLabel = useMemo(
    () => BASEMAPS.find((b) => b.id === activeId)?.label ?? 'Basemap',
    [activeId],
  );

  // Click outside closes the popover.
  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (popoverRef.current && !popoverRef.current.contains(target)) {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [open]);

  const select = (id: BasemapId) => {
    setBasemapToolCard({ activeBasemapId: id });
    savePreferredBasemapId(id);
    setOpen(false);
    if (!container) return;
    const map = getMapLibreHostHandle(container);
    if (map) {
      try {
        applyBasemap(map, id);
      } catch (err) {
        console.warn('[BasemapSwitcher] applyBasemap failed', err);
      }
    }
  };

  return (
    <div
      style={{
        position: 'absolute',
        right: tokens.space[4],
        bottom: tokens.space[6],
        zIndex: tokens.zIndex.panel,
        fontFamily: tokens.font.family.ui,
      }}
    >
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Basemap: ${activeLabel}`}
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: tokens.space[2],
          padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
          minHeight: tokens.density.touchMin,
          background: tokens.color.surface[1],
          color: tokens.color.ink.primary,
          border: tokens.border.thin,
          borderRadius: tokens.radius.input,
          cursor: 'pointer',
          fontSize: tokens.font.size.sm,
          boxShadow: tokens.elevation[1],
        }}
      >
        <Icon name="map" size={20} decorative />
        <span>{activeLabel}</span>
      </button>

      {open ? (
        <div
          ref={popoverRef}
          role="listbox"
          aria-label="Basemap"
          style={{
            position: 'absolute',
            right: 0,
            bottom: `calc(100% + ${tokens.space[2]}px)`,
            minWidth: tokens.layout.popoverSm,
            background: tokens.color.surface[1],
            color: tokens.color.ink.primary,
            border: tokens.border.thin,
            borderRadius: tokens.radius.md,
            boxShadow: tokens.elevation[3],
            padding: tokens.space[2],
            display: 'flex',
            flexDirection: 'column',
            gap: tokens.space[1],
          }}
        >
          {BASEMAPS.map((b) => {
            const isActive = b.id === activeId;
            return (
              <button
                key={b.id}
                type="button"
                role="option"
                aria-selected={isActive}
                onClick={() => select(b.id)}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  textAlign: 'left',
                  gap: tokens.space[2],
                  padding: tokens.space[2],
                  minHeight: tokens.density.touchMin,
                  background: isActive
                    ? tokens.color.accent.subtle
                    : 'transparent',
                  color: tokens.color.ink.primary,
                  border: isActive
                    ? `${tokens.border.width.thin}px solid ${tokens.color.accent.primary}`
                    : `${tokens.border.width.thin}px solid transparent`,
                  borderRadius: tokens.radius.input,
                  cursor: 'pointer',
                  fontSize: tokens.font.size.sm,
                }}
              >
                <Icon name={b.thumbnailIcon} size={20} decorative />
                <span style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontWeight: tokens.font.weight.medium }}>
                    {b.label}
                  </span>
                  <span
                    style={{
                      fontSize: tokens.font.size.xs,
                      color: tokens.color.ink.secondary,
                    }}
                  >
                    {b.description}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
